import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { IdSchema, type DashboardDimension, type ProjectDto } from '@taqon/contracts';
import { z } from 'zod';
import { useSearchParams } from 'react-router';
import { getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ProjectSearchPicker, useProject } from '../crm/common';

/**
 * The dimensions a dashboard and the lists behind it filter by (FRD-RPT-010), each named by its
 * key — the same key in the dashboard URL, the API query and the drill path — and the permission
 * that lets the session see the master's names.
 */
export const DIMENSIONS: readonly {
  readonly key: DashboardDimension;
  readonly label: string;
  readonly capability: string;
  /** The master list for a select; null for projects, which are searched on the server. */
  readonly path: string | null;
}[] = [
  {
    key: 'department_id',
    label: 'Department',
    capability: 'organization.department:view',
    path: '/api/v1/organization/departments',
  },
  {
    key: 'cost_center_id',
    label: 'Cost centre',
    capability: 'organization.cost_center:view',
    path: '/api/v1/organization/cost-centers',
  },
  {
    key: 'project_id',
    label: 'Project',
    capability: 'projects.project:view',
    path: null,
  },
  {
    key: 'location_id',
    label: 'Location',
    capability: 'organization.location:view',
    path: '/api/v1/organization/locations',
  },
  {
    key: 'supplier_id',
    label: 'Supplier',
    capability: 'procurement.supplier:view',
    path: '/api/v1/procurement/suppliers',
  },
  {
    key: 'employee_id',
    label: 'Employee',
    capability: 'hr.employee:view',
    path: '/api/v1/hr/employees',
  },
  {
    key: 'client_id',
    label: 'Client',
    capability: 'crm.client:view',
    path: '/api/v1/crm/clients',
  },
  {
    key: 'asset_id',
    label: 'Asset',
    capability: 'assets.asset:view',
    path: '/api/v1/assets/assets',
  },
];

/** A dimension master row: its code and its name (a company name for suppliers and clients). */
const MasterRowSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string().optional(),
  companyName: z.string().optional(),
});
type MasterRow = z.infer<typeof MasterRowSchema>;

/**
 * A dimension master, every status: a record filtered by an inactive department still belongs to
 * it. These masters are short in Release 1 (SDD §10.4 maximum page), so a select lists them all.
 */
function useMaster(path: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['reporting', 'dimension-master', path],
    enabled: enabled && path !== null,
    retry: false,
    queryFn: () => getPage(`${path ?? ''}?pageSize=200&sort=code`, MasterRowSchema),
  });
}

const describe = (row: MasterRow) => `${row.code} — ${row.name ?? row.companyName ?? ''}`;

/** One dimension's select (or, for projects, server-side search) bound to a URL value. */
function DimensionControl({
  dimension,
  value,
  onChange,
}: {
  dimension: (typeof DIMENSIONS)[number];
  value: string;
  onChange: (value: string) => void;
}) {
  const master = useMaster(dimension.path, true);
  const project = useProject(dimension.path === null && value !== '' ? value : null);
  if (dimension.path === null) {
    const chosen: ProjectDto | null =
      value !== '' && project.data !== undefined ? project.data : null;
    return (
      <ProjectSearchPicker
        label={dimension.label}
        value={chosen}
        onChange={(next) => {
          onChange(next?.id ?? '');
        }}
      />
    );
  }
  const rows = master.data?.items ?? [];
  // A value from the URL stays selectable while the list loads.
  const known = value === '' || rows.some((row) => row.id === value);
  return (
    <TextField
      select
      size="small"
      label={dimension.label}
      value={known ? value : ''}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      helperText={master.isError ? master.error.message : undefined}
      sx={{ minWidth: 200 }}
    >
      <MenuItem value="">All</MenuItem>
      {rows.map((row) => (
        <MenuItem key={row.id} value={row.id}>
          {describe(row)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * The dashboard's dimension filters, kept in the URL (FRD-RPT-010). Only the dimensions whose
 * master the session may view are offered; a value already in the URL for another dimension is
 * still applied and can be cleared.
 */
export function DimensionFilters({
  values,
  onChange,
}: {
  values: Partial<Record<DashboardDimension, string>>;
  onChange: (key: DashboardDimension, value: string) => void;
}) {
  const session = useSession();
  const offered = DIMENSIONS.filter((d) => hasCapability(session.data, d.capability));
  const hidden = DIMENSIONS.filter((d) => !offered.includes(d) && (values[d.key] ?? '') !== '');
  return (
    <Stack spacing={1.5}>
      {/* The same columns as the search card's filters, so every control fills its cell. */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
          '&& > *': { minWidth: 0 },
        }}
      >
        {offered.map((dimension) => (
          <DimensionControl
            key={dimension.key}
            dimension={dimension}
            value={values[dimension.key] ?? ''}
            onChange={(value) => {
              onChange(dimension.key, value);
            }}
          />
        ))}
      </Box>
      {hidden.map((dimension) => (
        <Alert
          key={dimension.key}
          severity="info"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                onChange(dimension.key, '');
              }}
            >
              Clear
            </Button>
          }
        >
          Filtered by a {dimension.label.toLowerCase()} you may not view by name.
        </Alert>
      ))}
    </Stack>
  );
}

/**
 * For a module list a dashboard drills into: states which dimension filters from the URL are
 * applied (FRD-RPT-007, RPT-010), by name where the session may view the master, with a way to
 * clear them. `keys` are the dimensions the list applies from the URL.
 */
export function UrlDimensionNote({ keys }: { keys: readonly DashboardDimension[] }) {
  const [params, setParams] = useSearchParams();
  const applied = DIMENSIONS.filter(
    (d) => keys.includes(d.key) && (params.get(d.key) ?? '') !== '',
  );
  if (applied.length === 0) return null;
  return (
    <Stack spacing={1}>
      {applied.map((dimension) => (
        <DimensionNote
          key={dimension.key}
          dimension={dimension}
          id={params.get(dimension.key) ?? ''}
          onClear={() => {
            const next = new URLSearchParams(params);
            next.delete(dimension.key);
            next.delete('page');
            setParams(next);
          }}
        />
      ))}
    </Stack>
  );
}

function DimensionNote({
  dimension,
  id,
  onClear,
}: {
  dimension: (typeof DIMENSIONS)[number];
  id: string;
  onClear: () => void;
}) {
  const session = useSession();
  const canView = hasCapability(session.data, dimension.capability);
  const master = useMaster(dimension.path, canView);
  const project = useProject(dimension.path === null ? id : null);
  const name =
    dimension.path === null
      ? project.data === undefined
        ? null
        : `${project.data.code} — ${project.data.name}`
      : (master.data?.items.find((row) => row.id === id) ?? null);
  const text = name === null ? 'the one chosen' : typeof name === 'string' ? name : describe(name);
  return (
    <Alert
      severity="info"
      action={
        <Button size="small" color="inherit" onClick={onClear}>
          Clear
        </Button>
      }
    >
      Filtered by {dimension.label.toLowerCase()}: {text}.
    </Alert>
  );
}
